import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

const initialState = {
  requirements: [],
  candidates: [],
  currentRequirement: null,
  currentCandidate: null,
  requirementStats: null,
  candidateStats: null,
  pagination: null,
  loading: false,
  error: null
};

// Requirements
export const fetchRequirements = createAsyncThunk(
  'hr/fetchRequirements',
  async ({ page = 1, limit = 10, status, search } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ page, limit });
      if (status) params.append('status', status);
      if (search) params.append('search', search);
      
      const response = await api.get(`/hr/requirements?${params}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch requirements');
    }
  }
);

export const fetchRequirementById = createAsyncThunk(
  'hr/fetchRequirementById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/hr/requirements/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch requirement');
    }
  }
);

export const createRequirement = createAsyncThunk(
  'hr/createRequirement',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/hr/requirements', data);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create requirement');
    }
  }
);

export const updateRequirement = createAsyncThunk(
  'hr/updateRequirement',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/hr/requirements/${id}`, data);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update requirement');
    }
  }
);

export const deleteRequirement = createAsyncThunk(
  'hr/deleteRequirement',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/hr/requirements/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete requirement');
    }
  }
);

// Candidates
export const fetchCandidates = createAsyncThunk(
  'hr/fetchCandidates',
  async ({ page = 1, limit = 10, requirement_id, status, search } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ page, limit });
      if (requirement_id) params.append('requirement_id', requirement_id);
      if (status) params.append('status', status);
      if (search) params.append('search', search);
      
      const response = await api.get(`/hr/candidates?${params}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch candidates');
    }
  }
);

export const fetchCandidateById = createAsyncThunk(
  'hr/fetchCandidateById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/hr/candidates/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch candidate');
    }
  }
);

export const createCandidate = createAsyncThunk(
  'hr/createCandidate',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/hr/candidates', data);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create candidate');
    }
  }
);

export const updateCandidate = createAsyncThunk(
  'hr/updateCandidate',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/hr/candidates/${id}`, data);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update candidate');
    }
  }
);

export const deleteCandidate = createAsyncThunk(
  'hr/deleteCandidate',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/hr/candidates/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete candidate');
    }
  }
);

// Stats
export const fetchRequirementStats = createAsyncThunk(
  'hr/fetchRequirementStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/hr/requirements/stats');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch stats');
    }
  }
);

export const fetchCandidateStats = createAsyncThunk(
  'hr/fetchCandidateStats',
  async (requirement_id, { rejectWithValue }) => {
    try {
      const params = requirement_id ? `?requirement_id=${requirement_id}` : '';
      const response = await api.get(`/hr/candidates/stats${params}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch stats');
    }
  }
);

const hrSlice = createSlice({
  name: 'hr',
  initialState,
  reducers: {
    clearCurrentRequirement: (state) => {
      state.currentRequirement = null;
    },
    clearCurrentCandidate: (state) => {
      state.currentCandidate = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Requirements
      .addCase(fetchRequirements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRequirements.fulfilled, (state, action) => {
        state.loading = false;
        state.requirements = action.payload.requirements;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchRequirements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Requirement By ID
      .addCase(fetchRequirementById.fulfilled, (state, action) => {
        state.currentRequirement = action.payload;
      })
      // Create Requirement
      .addCase(createRequirement.fulfilled, (state, action) => {
        state.requirements.unshift(action.payload);
      })
      // Update Requirement
      .addCase(updateRequirement.fulfilled, (state, action) => {
        const index = state.requirements.findIndex(r => r._id === action.payload._id);
        if (index !== -1) {
          state.requirements[index] = action.payload;
        }
        if (state.currentRequirement?._id === action.payload._id) {
          state.currentRequirement = action.payload;
        }
      })
      // Delete Requirement
      .addCase(deleteRequirement.fulfilled, (state, action) => {
        state.requirements = state.requirements.filter(r => r._id !== action.payload);
      })
      // Fetch Candidates
      .addCase(fetchCandidates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCandidates.fulfilled, (state, action) => {
        state.loading = false;
        state.candidates = action.payload.candidates;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchCandidates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Candidate By ID
      .addCase(fetchCandidateById.fulfilled, (state, action) => {
        state.currentCandidate = action.payload;
      })
      // Create Candidate
      .addCase(createCandidate.fulfilled, (state, action) => {
        state.candidates.unshift(action.payload);
      })
      // Update Candidate
      .addCase(updateCandidate.fulfilled, (state, action) => {
        const index = state.candidates.findIndex(c => c._id === action.payload._id);
        if (index !== -1) {
          state.candidates[index] = action.payload;
        }
        if (state.currentCandidate?._id === action.payload._id) {
          state.currentCandidate = action.payload;
        }
      })
      // Delete Candidate
      .addCase(deleteCandidate.fulfilled, (state, action) => {
        state.candidates = state.candidates.filter(c => c._id !== action.payload);
      })
      // Stats
      .addCase(fetchRequirementStats.fulfilled, (state, action) => {
        state.requirementStats = action.payload;
      })
      .addCase(fetchCandidateStats.fulfilled, (state, action) => {
        state.candidateStats = action.payload;
      });
  }
});

export const { clearCurrentRequirement, clearCurrentCandidate, clearError } = hrSlice.actions;
export default hrSlice.reducer;
